import React, { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";
import {
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  Clock,
  FileText,
  LifeBuoy,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  UsersRound,
  WalletCards,
} from "lucide-react";

import Footer from "./Footer";
import "./Contact.css";
import logo from "../components/Images/icon colored 7.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};

const contactOptions = [
  {
    icon: Mail,
    title: "Sales",
    text: "For pricing, demos, onboarding, and choosing the right plan.",
    href: "mailto:sales@kostpadi.com",
    action: "sales@kostpadi.com",
  },
  {
    icon: LifeBuoy,
    title: "Support",
    text: "For help with invoices, payroll, reports, inventory, roles, or errors.",
    href: "mailto:support@kostpadi.com",
    action: "support@kostpadi.com",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp",
    text: "Prefer quick chat? Send us a message during business hours.",
    href: "https://wa.me/2349031986790",
    action: "Chat on WhatsApp",
  },
  {
    icon: Phone,
    title: "Phone",
    text: "Speak with a human during local business hours.",
    href: "tel:+2349031986790",
    action: "+234 (903) 198 6790",
  },
];

const helpTopics = [
  "Pricing and plan selection",
  "Company setup and onboarding",
  "Invoices, receipts, and estimates",
  "Payroll and employee records",
  "Inventory and stock tracking",
  "Reports and accounting setup",
  "Team roles and access control",
  "CRM, booking, and scheduling",
];

const responseTargets = [
  { title: "Free / Starter", text: "1–2 business days" },
  { title: "SME", text: "Within 24 hours" },
  { title: "Business", text: "Priority response" },
];

const faqs = [
  {
    q: "Can I request a demo?",
    a: "Yes. You can use the contact form or demo link, and we’ll walk you through the features relevant to your business.",
  },
  {
    q: "Can you help us set up our company?",
    a: "Yes. We can guide you through company setup, users, invoices, payroll, inventory, and reporting.",
  },
  {
    q: "Do you support Nigerian business needs?",
    a: "Yes. KostPadi supports practical Nigerian SME workflows including VAT, WHT, payroll, invoices, receipts, vendor payments, and reports.",
  },
  {
    q: "What should I include in a support request?",
    a: "Include what you clicked, what you expected to happen, what happened instead, and screenshots where possible.",
  },
];

export default function Contact() {
  const [tab, setTab] = useState("sales");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    website: "",
    inquiry: "Sales",
    priority: "Normal",
    companySize: "1-5",
    message: "",
    agree: false,
  });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setSent(null);

    try {
      await emailjs.send(
        "YOUR_SERVICE_ID",
        "YOUR_TEMPLATE_ID",
        {
          from_name: form.fullName,
          reply_to: form.email,
          phone: form.phone,
          company: form.company,
          website: form.website,
          company_size: form.companySize,
          subject: `[${form.inquiry}] ${form.priority} • ${
            form.company || "No company"
          }`,
          message: form.message,
        },
        "YOUR_PUBLIC_KEY"
      );

      setSent("ok");
      setForm({
        fullName: "",
        email: "",
        phone: "",
        company: "",
        website: "",
        inquiry: tab === "sales" ? "Sales" : "Support",
        priority: "Normal",
        companySize: "1-5",
        message: "",
        agree: false,
      });
    } catch (err) {
      console.error(err);
      setSent("err");
    } finally {
      setSending(false);
    }
  };

  const switchTab = (nextTab) => {
    setTab(nextTab);
    setForm((f) => ({
      ...f,
      inquiry: nextTab === "sales" ? "Sales" : "Support",
    }));
  };

  return (
    <div className="contact-root">
      <header className="kp-container contact-hero" id="top">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="contact-hero-inner"
        >
          <motion.span variants={fadeInUp} className="contact-tag">
            Contact KostPadi
          </motion.span>

          <motion.h1 variants={fadeInUp}>
            Let’s help you choose, set up, or fix what you need.
          </motion.h1>

          <motion.p variants={fadeInUp} className="contact-lead">
            Whether you want a demo, pricing guidance, onboarding support, or
            help with an issue, our team is ready to assist.
          </motion.p>

          <motion.div variants={fadeInUp} className="contact-hero-actions">
            <a className="contact-btn contact-btn-primary" href="#contact-form">
              Send a message <ArrowRight size={18} />
            </a>
            <a className="contact-btn contact-btn-ghost" href="#book-demo">
              Book a demo
            </a>
          </motion.div>
        </motion.div>
      </header>

      <main>
        <section className="kp-container contact-section">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="contact-options-grid"
          >
            {contactOptions.map(({ icon: Icon, title, text, href, action }) => (
              <motion.a
                variants={fadeInUp}
                href={href}
                className="contact-option-card"
                key={title}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noreferrer" : undefined}
              >
                <div className="contact-icon-wrap">
                  <Icon size={22} />
                </div>
                <h3>{title}</h3>
                <p>{text}</p>
                <span>
                  {action} <ArrowRight size={16} />
                </span>
              </motion.a>
            ))}
          </motion.div>
        </section>

        <section id="contact-form" className="kp-container contact-section">
          <div className="contact-body">
            <div className="contact-form-wrap">
              <div
                className="contact-tabs"
                role="tablist"
                aria-label="Contact type"
              >
                <button
                  type="button"
                  className={tab === "sales" ? "active" : ""}
                  role="tab"
                  aria-selected={tab === "sales"}
                  onClick={() => switchTab("sales")}
                >
                  Sales / Demo
                </button>

                <button
                  type="button"
                  className={tab === "support" ? "active" : ""}
                  role="tab"
                  aria-selected={tab === "support"}
                  onClick={() => switchTab("support")}
                >
                  Support
                </button>
              </div>

              <form className="contact-form-panel" onSubmit={submit}>
                <div className="contact-form-grid">
                  <label>
                    Full name
                    <input
                      name="fullName"
                      value={form.fullName}
                      onChange={onChange}
                      placeholder="Your name"
                      required
                    />
                  </label>

                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="you@company.com"
                      required
                    />
                  </label>
                </div>

                <div className="contact-form-grid">
                  <label>
                    Company
                    <input
                      name="company"
                      value={form.company}
                      onChange={onChange}
                      placeholder="Company name"
                    />
                  </label>

                  <label>
                    Phone
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="+234..."
                    />
                  </label>
                </div>

                <div className="contact-form-grid">
                  <label>
                    Website
                    <input
                      name="website"
                      value={form.website}
                      onChange={onChange}
                      placeholder="https://..."
                    />
                  </label>

                  <label>
                    Company size
                    <select
                      name="companySize"
                      value={form.companySize}
                      onChange={onChange}
                    >
                      <option>1-5</option>
                      <option>6-20</option>
                      <option>21-50</option>
                      <option>51-100</option>
                      <option>100+</option>
                    </select>
                  </label>
                </div>

                <div className="contact-form-grid">
                  {tab === "support" ? (
                    <>
                      <label>
                        Issue type
                        <select
                          name="inquiry"
                          value={form.inquiry}
                          onChange={onChange}
                        >
                          <option>Support</option>
                          <option>Invoices / Receipts</option>
                          <option>Accounting / Reports</option>
                          <option>Payroll</option>
                          <option>Inventory</option>
                          <option>Team & Roles</option>
                          <option>CRM / Scheduling</option>
                          <option>Billing</option>
                          <option>Technical</option>
                        </select>
                      </label>

                      <label>
                        Priority
                        <select
                          name="priority"
                          value={form.priority}
                          onChange={onChange}
                        >
                          <option>Low</option>
                          <option>Normal</option>
                          <option>High</option>
                          <option>Urgent</option>
                        </select>
                      </label>
                    </>
                  ) : (
                    <>
                      <label>
                        Interest
                        <select
                          name="inquiry"
                          value={form.inquiry}
                          onChange={onChange}
                        >
                          <option>Sales</option>
                          <option>Pricing</option>
                          <option>Demo Request</option>
                          <option>Partnership</option>
                          <option>Onboarding</option>
                        </select>
                      </label>

                      <label>
                        Timeline
                        <select
                          name="priority"
                          value={form.priority}
                          onChange={onChange}
                        >
                          <option>Normal</option>
                          <option>This week</option>
                          <option>This month</option>
                          <option>Just researching</option>
                        </select>
                      </label>
                    </>
                  )}
                </div>

                <label>
                  Message
                  <textarea
                    name="message"
                    rows="5"
                    value={form.message}
                    onChange={onChange}
                    placeholder={
                      tab === "sales"
                        ? "Tell us what your business does and what you want KostPadi to help with..."
                        : "Describe the issue, what you clicked, and what happened..."
                    }
                    required
                  />
                </label>

                <div className="contact-form-footer">
                  <label className="contact-agree">
                    <input
                      type="checkbox"
                      name="agree"
                      checked={form.agree}
                      onChange={onChange}
                      required
                    />
                    <span>
                      I agree to the processing of my data as described in the
                      Privacy Policy.
                    </span>
                  </label>

                  <button
                    className="contact-btn contact-btn-primary"
                    type="submit"
                    disabled={sending}
                  >
                    {sending
                      ? "Sending..."
                      : tab === "sales"
                      ? "Contact Sales"
                      : "Contact Support"}
                  </button>
                </div>

                {sent === "ok" && (
                  <p className="form-success">
                    Thanks! We’ll get back to you shortly.
                  </p>
                )}

                {sent === "err" && (
                  <p className="form-error">
                    Something went wrong. Please try again.
                  </p>
                )}

                <p className="contact-tiny-note">
                  Tip: for support requests, include screenshots, invoice IDs,
                  payroll run details, or the page where the issue happened.
                </p>
              </form>
            </div>

            <aside className="contact-aside">
              <div className="contact-aside-card contact-brand-card">
                <img src={logo} alt="KostPadi" />
                <h3>Talk to our team</h3>
                <p>
                  We are based in Lagos and support businesses across Nigeria.
                  Our normal hours are Monday to Friday, 9am–6pm WAT.
                </p>

                <div className="contact-mini-list">
                  <a href="mailto:sales@kostpadi.com">
                    <Mail size={17} />
                    sales@kostpadi.com
                  </a>
                  <a href="mailto:support@kostpadi.com">
                    <LifeBuoy size={17} />
                    support@kostpadi.com
                  </a>
                  <a href="tel:+2349031986790">
                    <Phone size={17} />
                    +234 (903) 198 6790
                  </a>
                  <a
                    href="https://wa.me/2349031986790"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MessageCircle size={17} />
                    WhatsApp
                  </a>
                </div>
              </div>

              <div id="book-demo" className="contact-aside-card">
                <div className="contact-icon-wrap soft">
                  <CalendarCheck size={22} />
                </div>
                <h3>Book a demo</h3>
                <p>
                  We’ll show you how KostPadi can work for invoicing, payroll,
                  inventory, reports, team roles, CRM, and other business needs.
                </p>

                <a
                  className="contact-btn contact-btn-ghost"
                  href="https://calendly.com/oluwatobi-a-tssynergyconsult/30min"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open scheduler
                </a>
              </div>

              <div className="contact-aside-card">
                <div className="contact-icon-wrap soft">
                  <Clock size={22} />
                </div>
                <h3>Response targets</h3>

                <div className="contact-response-list">
                  {responseTargets.map((item) => (
                    <div key={item.title}>
                      <strong>{item.title}</strong>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="contact-aside-card">
                <div className="contact-icon-wrap soft">
                  <MapPin size={22} />
                </div>
                <h3>Office location</h3>
                <p>Lagos, Nigeria</p>

                <a
                  className="contact-btn contact-btn-ghost"
                  href="https://maps.google.com/?q=Lagos%2C+Nigeria"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Maps
                </a>
              </div>
            </aside>
          </div>
        </section>

        <section className="contact-section contact-section-alt">
          <div className="kp-container">
            <div className="contact-section-head">
              <span className="contact-mini-tag">What we can help with</span>
              <h2>Get practical help across the full KostPadi platform.</h2>
              <p>
                From setup to daily use, our team can point you in the right
                direction.
              </p>
            </div>

            <div className="contact-topic-grid">
              {helpTopics.map((topic) => (
                <div className="contact-topic-card" key={topic}>
                  <CheckCircle2 size={18} />
                  <span>{topic}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="kp-container contact-section">
          <div className="contact-trust-grid">
            <TrustCard
              icon={ShieldCheck}
              title="Privacy-first support"
              text="We only ask for the details needed to understand and resolve your request."
            />
            <TrustCard
              icon={FileText}
              title="Better setup guidance"
              text="We can guide you through company setup, reports, payroll, inventory, and team access."
            />
            <TrustCard
              icon={WalletCards}
              title="Built for Nigerian SMEs"
              text="We understand VAT, WHT, payroll, receipts, invoices, and practical local business workflows."
            />
            <TrustCard
              icon={UsersRound}
              title="Team-friendly onboarding"
              text="We help you decide which user roles your team members should have."
            />
          </div>
        </section>

        <section className="contact-section contact-section-alt">
          <div className="kp-container">
            <div className="contact-section-head">
              <span className="contact-mini-tag">Questions</span>
              <h2>Common questions</h2>
            </div>

            <div className="contact-faq-grid">
              {faqs.map((faq) => (
                <div className="contact-faq-item" key={faq.q}>
                  <h3>{faq.q}</h3>
                  <p>{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function TrustCard({ icon: Icon, title, text }) {
  return (
    <div className="contact-trust-card">
      <div className="contact-icon-wrap soft">
        <Icon size={22} />
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}
